import type { JSX } from 'preact';
import { setPanel } from '../panels.ts';
import {
  EMPTY_PERF_READOUT,
  formatMs,
  formatUploadBytes,
  type PerfReadout,
} from '../play/perfReadout.ts';
import './perfPanel.css';

/**
 * View → Perf. The same numbers play shows under Debug → Perf, on the editor's own plate
 * rather than the play debug chrome (docs/PLAY-HUD.md: the two do not borrow each other's
 * styling). The top-right FPS integer stays where it is; this is the render breakdown.
 *
 * `ScenePerf` throttles the push to four times a second, so this never re-renders per frame.
 */
export type EditorPerfProps = {
  perf: PerfReadout;
};

export type EditorPerf = {
  sync: (state: EditorPerfProps) => void;
  destroy: () => void;
};

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div class="editor-perf-row">
      <span class="editor-perf-label">{label}</span>
      <span class="editor-perf-value">{value}</span>
    </div>
  );
}

export function EditorPerfPanel({ perf }: EditorPerfProps): JSX.Element {
  return (
    <div
      class="editor-perf ui-panel"
      aria-label="Render performance"
      data-perf-frames={perf.frames}
    >
      <Row label="JS" value={`${formatMs(perf.jsMs)} · p90 ${formatMs(perf.jsP90Ms)}`} />
      <Row label="GPU" value={formatMs(perf.gpuMs)} />
      <Row label="Draws" value={`${perf.drawCalls} · ${perf.triangles} tris`} />
      <Row label="Upload" value={formatUploadBytes(perf.uploadBytes)} />
      <Row label="FBO" value={String(perf.fboBinds)} />
    </div>
  );
}

export function mountEditorPerf(): EditorPerf {
  setPanel('editorPerf', { perf: EMPTY_PERF_READOUT });

  return {
    sync: (state) => setPanel('editorPerf', state),
    destroy: () => setPanel('editorPerf', null),
  };
}
